import React from 'react';
import { Box, Button, Grid, Skeleton, Typography } from '@mui/material';
import Table from '../../component/table/table';
import { MUIDataTableColumnDef } from 'mui-datatables';
import _ from 'lodash';
import GlobalNotification from '../../component/common/snackbar';
import i18n from '../../i18n';
import DialogComponent from '../../component/dialog/dialog.component';
import BaseInput from '../../component/form/base-input.component';
import { FormFieldCell } from '../../component/form/form-field-cell.component';
import { apiManage_rbp } from '../../request';
import AlertDialog from '../../component/dialog/alert-dialog.component';
import ButtonComponent from '../../component/button/button.component';
import { TableMarker } from '../../models/table-marker.entity';
import setLoading from '../../component/linear-determinate';
import './scss/table-markers.page.scss';

enum TableMarkerDialogType {
    ADD = 'ADD',
    BATCH = 'BATCH',
}

/**
 * 云 RBP「桌牌」设置：列表、单条新增、批量新增、批量删除；已绑定订单的桌牌不可勾选删除。
 * 布局对齐 Figma 1687:24265：标题单行、其下操作行、白底表格区与行间分割线。
 */
class TableMarkers extends React.Component<Props, State> {
    columns: MUIDataTableColumnDef[] = [
        {
            name: 'id',
            label: i18n.t('table_markers_id'),
            options: {
                setCellHeaderProps: () => ({ className: 'table-markers-page__cell-id' }),
                setCellProps: () => ({ className: 'table-markers-page__cell-id' }),
            },
        },
        {
            name: 'table_marker_num',
            label: i18n.t('table_markers_number'),
            options: {
                setCellHeaderProps: () => ({ className: 'table-markers-page__cell-num' }),
                setCellProps: () => ({ className: 'table-markers-page__cell-num' }),
            },
        },
        /** 占位列：吸收剩余宽度，避免两列数据被拉得过宽 */
        {
            name: '__pad',
            label: '\u00a0',
            options: {
                sort: false,
                filter: false,
                searchable: false,
                download: false,
                setCellHeaderProps: () => ({ className: 'table-markers-page__cell-pad' }),
                setCellProps: () => ({ className: 'table-markers-page__cell-pad' }),
                customBodyRender: () => null,
            },
        },
    ];

    public state: State = {
        tableMarkers: [],
        rowsSelected: [],
        rowsSelectedItem: [],
        currPage: 0,
        rowsPerPage: 20,
        dialogOpen: false,
        dialogType: null,
        tableMarkerNum: '',
        mulAddTableNum: 1,
        isEmpty: 0,
        deleteOpen: false,
        listLoading: false,
    };

    componentDidMount() {
        void this.init();
    }

    async init() {
        this.setState({ listLoading: true });
        setLoading(true);
        try {
            const res = await apiManage_rbp.get_table_markers();
            if (res && !_.get(res, 'error')) {
                const list = Array.isArray(res) ? (res as TableMarker[]) : [];
                this.setState({ tableMarkers: list, rowsSelected: [], rowsSelectedItem: [] });
            } else {
                this.setState({ tableMarkers: [], rowsSelected: [], rowsSelectedItem: [] });
            }
        } catch {
            this.setState({ tableMarkers: [] });
        } finally {
            setLoading(false);
            this.setState({ listLoading: false });
        }
    }

    getDisableCheckBoxIndices(): number[] {
        return (this.state.tableMarkers || []).reduce((pre: number[], cur, index) => {
            if (cur.order_head_id) {
                pre.push(index);
            }
            return pre;
        }, []);
    }

    getRowItem(selectIndex: number | number[], disableCheckBoxRow: number[]): TableMarker | number[] | undefined {
        const list = _.cloneDeep(this.state.tableMarkers);
        if (typeof selectIndex === 'number') {
            return list.find((_, i) => i === selectIndex);
        }
        if (Array.isArray(selectIndex)) {
            return list.reduce((pre: number[], cur, index) => {
                if (selectIndex.includes(index) && !disableCheckBoxRow.includes(index)) {
                    pre.push(cur.id);
                }
                return pre;
            }, []);
        }
        return undefined;
    }

    closeDialog = () => {
        this.setState({
            dialogOpen: false,
            dialogType: null,
            tableMarkerNum: '',
            mulAddTableNum: 1,
            isEmpty: 0,
        });
    };

    onCreateTableMarker = async (isBatch = false) => {
        const { tableMarkerNum, mulAddTableNum } = this.state;
        if (!isBatch) {
            if (!String(tableMarkerNum).trim()) {
                this.setState({ isEmpty: 1 });
                return;
            }
            const n = Number(tableMarkerNum);
            if (n <= 0 || n > 9999) {
                GlobalNotification.error(i18n.t('input_rule_text_must_be_an_integer_greater_than_0_and_less_than_10000'));
                return;
            }
        } else {
            const c = Number(mulAddTableNum);
            if (!c || c <= 0 || c > 2000) {
                GlobalNotification.error(i18n.t('table_markers_batch_count_invalid'));
                return;
            }
        }
        try {
            setLoading(true);
            const res = await apiManage_rbp.create_table_markers({
                table_marker_num: isBatch ? 0 : Number(tableMarkerNum),
                isBatch,
                count: Number(mulAddTableNum),
            });
            setLoading(false);
            if (res && !_.get(res, 'error')) {
                GlobalNotification.success(i18n.t('google_text_create_succeed'));
                this.closeDialog();
                await this.init();
            } else {
                GlobalNotification.error(i18n.t('google_text_create_faile'));
            }
        } catch {
            setLoading(false);
            GlobalNotification.error(i18n.t('google_text_create_faile'));
        }
    };

    onDeleteTableMarkers = async () => {
        const { rowsSelectedItem } = this.state;
        if (!rowsSelectedItem.length) {
            this.setState({ deleteOpen: false });
            return;
        }
        try {
            setLoading(true);
            const res = await apiManage_rbp.delete_table_markers(rowsSelectedItem);
            setLoading(false);
            if (res && !_.get(res, 'error')) {
                GlobalNotification.success(i18n.t('google_text_delete_success'));
            } else {
                GlobalNotification.error(i18n.t('google_text_delete_failed'));
            }
        } catch {
            setLoading(false);
            GlobalNotification.error(i18n.t('google_text_delete_failed'));
        }
        this.setState({ deleteOpen: false, rowsSelected: [], rowsSelectedItem: [] });
        await this.init();
    };

    renderDialogTitle(): string {
        const { dialogType } = this.state;
        if (dialogType === TableMarkerDialogType.BATCH) {
            return i18n.t('table_markers_batch_add_numbers');
        }
        return i18n.t('table_markers_add_number');
    }

    renderDialogContent(): React.ReactNode {
        const { dialogType, tableMarkerNum, mulAddTableNum, isEmpty } = this.state;
        if (dialogType === TableMarkerDialogType.BATCH) {
            return (
                <Grid container>
                    <Grid item xs={12}>
                        <FormFieldCell labelKey='table_markers_add_amount'>
                            <BaseInput
                                hiddenLabel
                                variant='outlined'
                                required
                                regularType='required'
                                type='number'
                                value={mulAddTableNum}
                                fullWidth
                                size='small'
                                className='table-markers-page__dialog-field'
                                onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                    const value = Number(e.target.value);
                                    if (value > 0 && value <= 2000) {
                                        this.setState({ mulAddTableNum: value });
                                    }
                                }}
                            />
                        </FormFieldCell>
                    </Grid>
                </Grid>
            );
        }
        return (
            <Grid container>
                <Grid item xs={12}>
                    <FormFieldCell labelKey='table_markers_number'>
                        <BaseInput
                            hiddenLabel
                            variant='outlined'
                            required
                            regularType='required'
                            type='number'
                            value={tableMarkerNum}
                            fullWidth
                            size='small'
                            isEmpty={!!isEmpty}
                            className='table-markers-page__dialog-field'
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                                const value = e.target.value;
                                if (/^\d{0,4}$/.test(value)) {
                                    this.setState({ tableMarkerNum: value });
                                }
                            }}
                        />
                    </FormFieldCell>
                </Grid>
            </Grid>
        );
    }

    render() {
        const {
            tableMarkers,
            rowsSelected,
            currPage,
            rowsPerPage,
            dialogOpen,
            dialogType,
            deleteOpen,
            rowsSelectedItem,
            listLoading,
        } = this.state;
        const disableCheckBoxRow = this.getDisableCheckBoxIndices();

        return (
            <Box className='table-markers-page'>
                <Box className='table-markers-page__shell'>
                    <Typography className='table-markers-page__title' component='h2'>
                        {i18n.t('table_markers')}
                    </Typography>
                    <Box className='table-markers-page__toolbar'>
                        <Button
                            type='button'
                            variant='contained'
                            disableElevation
                            className='table-markers-page__btn-primary'
                            onClick={() =>
                                this.setState({
                                    dialogOpen: true,
                                    dialogType: TableMarkerDialogType.ADD,
                                    tableMarkerNum: '',
                                    isEmpty: 0,
                                })
                            }
                        >
                            {i18n.t('table_markers_add_number')}
                        </Button>
                        <Button
                            type='button'
                            variant='text'
                            color='inherit'
                            className='table-markers-page__btn-secondary'
                            onClick={() =>
                                this.setState({
                                    dialogOpen: true,
                                    dialogType: TableMarkerDialogType.BATCH,
                                    mulAddTableNum: 1,
                                })
                            }
                        >
                            {i18n.t('table_markers_batch_add_numbers')}
                        </Button>
                        <Button
                            type='button'
                            variant='text'
                            color='inherit'
                            disabled={rowsSelectedItem.length === 0}
                            className='table-markers-page__btn-secondary table-markers-page__btn-delete'
                            aria-label={i18n.t('table_markers_delete_num')}
                            onClick={() => this.setState({ deleteOpen: true })}
                        >
                            {i18n.t('table_markers_toolbar_delete')}
                        </Button>
                    </Box>

                    <Box className='table-markers-page__table-card'>
                        {listLoading ? (
                            <Box sx={{ px: 2, py: 1.5 }}>
                                <Skeleton variant='rounded' height={36} sx={{ mb: 1.5 }} />
                                {Array.from({ length: 8 }).map((_, index) => (
                                    <Skeleton
                                        key={`table-markers-row-skeleton-${index}`}
                                        variant='rounded'
                                        height={32}
                                        sx={{ mb: 1 }}
                                    />
                                ))}
                            </Box>
                        ) : (
                            <Table
                                centerCellContent
                                data={tableMarkers || []}
                                columns={this.columns}
                                selectableRowsHeader
                                selectableRowsHideCheckboxes={false}
                                pagination
                                rowsPerPage={rowsPerPage}
                                page={currPage}
                                rowsSelected={rowsSelected}
                                onChangePage={(page: number) => this.setState({ currPage: page })}
                                onRowSelectionChange={(_c, _a, rowsSel?: number[]) => {
                                    const filtered = (rowsSel || []).filter((i) => !disableCheckBoxRow.includes(i));
                                    const ids = this.getRowItem(filtered, disableCheckBoxRow) as number[];
                                    this.setState({
                                        rowsSelected: filtered,
                                        rowsSelectedItem: ids || [],
                                    });
                                }}
                                otherOption={{
                                    onChangeRowsPerPage: (nums: number) => {
                                        this.setState({ rowsPerPage: nums, currPage: 0 });
                                    },
                                }}
                                disableCheckBoxRow={disableCheckBoxRow}
                            />
                        )}
                    </Box>
                </Box>

                <DialogComponent
                    open={dialogOpen}
                    fullWidth
                    maxWidth='sm'
                    onClose={this.closeDialog}
                    defaultHeaderProps={{
                        title: this.renderDialogTitle(),
                        onClose: this.closeDialog,
                    }}
                    footer={
                        <Box>
                            <Button color='primary' variant='outlined' onClick={this.closeDialog}>
                                {i18n.t('google_text_public_button_cancel')}
                            </Button>
                            <Box marginLeft={2} display='inline-block'>
                                <ButtonComponent
                                    variant='contained'
                                    titleText={i18n.t('google_text_confirm')}
                                    handleClick={async () => {
                                        if (dialogType === TableMarkerDialogType.BATCH) {
                                            await this.onCreateTableMarker(true);
                                        } else {
                                            await this.onCreateTableMarker(false);
                                        }
                                    }}
                                >
                                    {i18n.t('google_text_confirm')}
                                </ButtonComponent>
                            </Box>
                        </Box>
                    }
                >
                    <Box sx={{ pt: 1 }}>{this.renderDialogContent()}</Box>
                </DialogComponent>

                {deleteOpen && (
                    <AlertDialog
                        open
                        variant="delete"
                        size='xs'
                        title={i18n.t('table_markers_delete_num')}
                        desc={i18n.t('dinner_table_page_whether_delete')}
                        onCancel={() => this.setState({ deleteOpen: false })}
                        onConfirm={() => void this.onDeleteTableMarkers()}
                    />
                )}
            </Box>
        );
    }
}

export default TableMarkers;

type State = {
    tableMarkers: TableMarker[];
    rowsSelected: number[];
    rowsSelectedItem: number[];
    currPage: number;
    rowsPerPage: number;
    dialogOpen: boolean;
    dialogType: TableMarkerDialogType | null;
    tableMarkerNum: string;
    mulAddTableNum: number;
    isEmpty: number;
    deleteOpen: boolean;
    listLoading: boolean;
};

type Props = Record<string, never>;
